import React from "react";
import {useTranslation} from "react-i18next";
import Modal from "@sharedComponents/Modal";
import Alert from "@sharedComponents/Alert";
import Button from "@sharedComponents/Button";
import {ButtonType} from "@sharedComponents/Button/types";
import InputText from "@sharedComponents/InputText";
import FlyersService from "@apps/Shared/Services/Flyers";
import {StatusCode} from "@apps/Shared/Services/Response.types";
import {FormErrorType} from "@sharedComponents/PantaForm/types";
import {getAllErrors} from "@sharedComponents/PantaForm/utils";
import {OrderAssetFormData, OrderAssetModalProps} from "./types";
import {validateEmail, validatePostalCode} from "./validation";
import "./Scss/order-asset-modal.scss";
import {StoreFlyerType} from "@sharedComponents/StoreFlyers/types";

const OrderAssetModal = ({onClose}: OrderAssetModalProps) => {
    const {t} = useTranslation();
    const [formData, setFormData] = React.useState<OrderAssetFormData>({
        amount: "",
        name: "",
        email: "",
        address: "",
        postalCode: "",
        city: ""
    });
    const [formErrors, setFormErrors] = React.useState<FormErrorType[]>([]);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [requestErrors, setRequestErrors] = React.useState<string[]>([]);
    const [submitting, setSubmitting] = React.useState(false);
    const allErrors = getAllErrors(formErrors);
    const hasValidationErrors = !!(allErrors.length > 0);
    const hasRequestErrors = requestErrors.length > 0;

    const handleChange =
        (field: keyof OrderAssetFormData) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                setFormData(current => ({
                    ...current,
                    [field]: event.target.value
                }));
            };

    const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setFormData(current => ({
            ...current,
            email: value
        }));
        setFormErrors(currentErrors => validateEmail(value, currentErrors));
    };

    const handleChangePostalCode = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setFormData(current => ({
            ...current,
            postalCode: value
        }));
        setFormErrors(currentErrors => validatePostalCode(value, currentErrors));
    };

    const isSubmitDisabled = Object.values(formData).some(value => !value) || submitting;

    const handleOrder = async () => {
        if (hasValidationErrors) {
            return;
        }

        setSubmitting(true);
        setRequestErrors([]);

        const shippingAddress = `${formData.address}, ${formData.postalCode} ${formData.city}`;
        const response = await FlyersService.OrderStoreFlyer({
            amountOrdered: parseInt(formData.amount, 10),
            shippingAddress: shippingAddress,
            flyerType: StoreFlyerType.Vippa,
            name: formData.name,
            email: formData.email
        });

        if (response?.status === StatusCode.OK) {
            setShowConfirmation(true);
            setSubmitting(false);
            return;
        }

        setRequestErrors(response?.data?.errors || [t("error.somethingwrong")]);
        setSubmitting(false);
    };

    return (
        <Modal open={true} size="lg" handleClose={onClose}>
            <div className={"order-asset-modal"}>
                {showConfirmation ? (
                    <div className="order-asset-modal__confirmation">
                        <h3 className={"heading heading--center"}>
                            {t("downloadAssets.orderModal.confirmationHeading")}
                        </h3>
                        <p className="order-asset-modal__description">
                            {t("downloadAssets.orderModal.confirmationDescription")}
                        </p>
                    </div>
                ) : (
                    <>
                        <h3 className={"heading heading--center"}>{t("downloadAssets.orderModal.heading")}</h3>

                        <p className="order-asset-modal__description">{t("downloadAssets.orderModal.description")}</p>

                        <div className="order-asset-modal__input-fields">
                            <InputText
                                id="download-assets-order-amount"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.amount")}
                                value={formData.amount}
                                type="number"
                                required={true}
                                onChange={handleChange("amount")}
                            />
                            <p className={"order-asset-modal__contact-info"}>{t("downloadAssets.orderModal.contactInfo")}</p>
                            <InputText
                                id="download-assets-order-name"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.name")}
                                value={formData.name}
                                required={true}
                                onChange={handleChange("name")}
                            />
                            <InputText
                                id="download-assets-order-email"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.email")}
                                value={formData.email}
                                type="email"
                                required={true}
                                onChange={handleChangeEmail}
                                hasError={!!formErrors.find(err => err.key === "email")}
                            />
                            <InputText
                                id="download-assets-order-address"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.address")}
                                value={formData.address}
                                required={true}
                                onChange={handleChange("address")}
                            />
                            <InputText
                                id="download-assets-order-postal-code"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.postalCode")}
                                value={formData.postalCode}
                                required={true}
                                onChange={handleChangePostalCode}
                                hasError={!!formErrors.find(err => err.key === "zipCode")}
                            />
                            <InputText
                                id="download-assets-order-city"
                                className="order-asset-modal__field"
                                label={t("downloadAssets.orderModal.city")}
                                value={formData.city}
                                required={true}
                                onChange={handleChange("city")}
                            />
                        </div>
                        {hasValidationErrors && (
                            <div className="order-asset-modal__error-messages">
                                <Alert
                                    type="danger"
                                    text={t("compensation.formErrors")}
                                    subtext={allErrors}
                                    compact={false}
                                />
                            </div>
                        )}
                        {hasRequestErrors && (
                            <div className="order-asset-modal__error-messages">
                                <Alert
                                    type="danger"
                                    text={t("error.somethingwrong")}
                                    subtext={requestErrors}
                                    compact={false}
                                />
                            </div>
                        )}
                        <Button
                            text={t("downloadAssets.orderModal.orderButton")}
                            type={ButtonType.PRIMARY}
                            large={true}
                            onClick={handleOrder}
                            disabled={isSubmitDisabled}
                            loading={submitting}
                        />
                    </>
                )}
            </div>

        </Modal>
    );
};

export default OrderAssetModal;
